import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClub } from '@/contexts/ClubContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchClubsForUser } from '@/lib/clubMembership';
import { API_BASE as API_BASE_URL } from '@/services/apiClient';
import placeholderImage from '@/assets/aiv-logo-trans.png';
import aivLogo from '@/assets/aiv-logo-trans.png';
import { useI18n } from '@/contexts/I18nContext';

type ClubLite = { id: number; name: string; slogan?: string | null; logo_url?: string | null };

const API_BASE = API_BASE_URL;

const normalizeLogoUrl = (raw?: string | null) => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const rebuild = (path: string) => (API_BASE ? `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}` : path);
  try {
    const parsed = new URL(trimmed);
    const pathWithSearch = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return rebuild(pathWithSearch);
  } catch {
    return rebuild(trimmed);
  }
};

export default function SelectClub() {
  const { t } = useI18n();
  const { selectClub, refreshData } = useClub();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<ClubLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchClubsForUser(user?.id, {
          accountType: user?.accountType,
          skipMemberEnumeration: (user?.accountType || '').toUpperCase() === 'ADMIN',
        });
        setClubs(list || []);
        if (list && list.length === 1) {
          await selectClub(list[0].id);
          await refreshData(list[0].id);
          navigate('/dashboard');
          return;
        }
      } catch (err) {
        toast.error(t('select_club_failed_load', 'Failed to load clubs'));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, selectClub, user, t]);

  const handleSelect = async (id: number) => {
    setSelectingId(id);
    try {
      await selectClub(id);
      await refreshData(id);
      navigate('/dashboard');
    } finally {
      setSelectingId(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('select_club_loading', 'Loading clubs...')}</div>;
  }

  if (!clubs.length) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-10">
            <p className="text-muted-foreground">{t('select_club_none', 'You are not a member of any clubs yet.')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
      {selectingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-background/70">
          <div className="flex flex-col items-center gap-3 px-6 py-5 rounded-xl border border-border/60 shadow-2xl bg-card">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t('select_club_entering', 'Entering club...')}</p>
          </div>
        </div>
      )}
      <div className="w-full max-w-3xl space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl text-primary-foreground">
            <img
              style={{ height: '130px', width: '130px' }}
              src={aivLogo}
              alt={t('brand_logo_alt', 'AIV Logo')}
              className="object-contain"
            />
          </div>
          <h1 className="text-3xl font-display font-bold">{t('select_club_title', 'Select a Club')}</h1>
          <p className="text-muted-foreground">{t('select_club_subtitle', 'Choose which club to manage or play in')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {clubs.map((c) => (
            <Card key={c.id} className="hover:border-primary/70 transition-all">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <img
                    src={c.logo_url ? normalizeLogoUrl(c.logo_url) : placeholderImage}
                    alt={c.name}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.dataset.fallbackApplied === 'true') return;
                      e.currentTarget.dataset.fallbackApplied = 'true';
                      e.currentTarget.src = placeholderImage;
                    }}
                  />
                  <span>{c.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">{c.slogan || t('select_club_no_slogan', 'No slogan set')}</div>
                <Button onClick={() => handleSelect(c.id)} variant="outline" disabled={selectingId !== null}>
                  {selectingId === c.id ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> {t('select_club_entering', 'Entering club...')}
                    </span>
                  ) : (
                    t('select_club_enter', 'Enter')
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
